/* eslint-disable react/jsx-props-no-spreading */
import {
  FullscreenControl,
  GeolocateControl,
  Map,
  MapRef,
} from "@vis.gl/react-maplibre";
import * as maplibre from "maplibre-gl";
import React, { ComponentProps, useEffect, useRef } from "react";
import isEqual from "lodash/isEqual";

const SQUAREFOOT_HQ = { latitude: 40.712784, longitude: -74.005941 };
const VIEWPORT_OFFSET = 65;
const VIEWPORT_PADDING = {
  top: VIEWPORT_OFFSET,
  right: VIEWPORT_OFFSET,
  bottom: VIEWPORT_OFFSET,
  left: VIEWPORT_OFFSET,
};

interface BaseMapProps extends ComponentProps<typeof Map> {
  className?: string;
  autoCenter?: boolean;
}

function BaseMap({
  children,
  autoCenter,
  initialViewState,
  ...restProps
}: BaseMapProps) {
  const ref = useRef<MapRef>(null);
  const lastBounds = useRef<maplibre.LngLatBounds>();

  useEffect(() => {
    requestAnimationFrame(() => {
      const map = ref.current?.getMap();
      if (!map || !autoCenter) return;

      const bounds = new maplibre.LngLatBounds();

      React.Children.forEach(children, (child) => {
        const props = child.props || child.attributes;

        if ("latitude" in props && "longitude" in props) {
          bounds.extend(new maplibre.LngLat(props.longitude, props.latitude));
        } else if ("data" in props && "coordinates" in props.data) {
          const coordinates = Array.isArray(props.data.coordinates)
            ? props.data.coordinates
            : [];

          const extendBounds = (coords: any[]) => {
            if (!Array.isArray(coords)) return;

            // eslint-disable-next-line no-restricted-syntax
            for (const c of coords) {
              if (Array.isArray(c)) {
                if (c.length === 2) {
                  bounds.extend(maplibre.LngLat.convert(c as never));
                } else {
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  extendBounds(c);
                }
              }
            }
          };

          extendBounds(coordinates);
        }
      });

      if (
        !bounds.isEmpty() &&
        !isEqual(bounds.toArray(), lastBounds.current?.toArray())
      ) {
        lastBounds.current = bounds;
        map.fitBounds(bounds, {
          speed: 3,
          minZoom: 12,
          linear: true,
        });
      }
    });
  }, [children, autoCenter]);

  return (
    <Map
      ref={ref}
      maxZoom={18}
      cooperativeGestures
      {...restProps}
      initialViewState={{
        ...SQUAREFOOT_HQ,
        zoom: 16,
        padding: VIEWPORT_PADDING,
        ...initialViewState,
      }}
      mapStyle="https://tiles.openfreemap.org/styles/bright"
    >
      <FullscreenControl position="top-right" />
      <GeolocateControl />
      {children}
    </Map>
  );
}

export const LinePaintOptions = {
  "line-color": "#004f8a",
  "line-width": 2,
};

export const FillPaintOptions = {
  "fill-color": "#004f8a",
  "fill-opacity": 0.4,
};

export * from "@vis.gl/react-maplibre";
export default BaseMap;
